import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { X, Loader2, MessageCircle, AlertTriangle } from "lucide-react";

function OptionList({ options, selected, onSelect, disabled }) {
  return (
    <div className={`space-y-2 ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
      {options.map((o) => (
        <label key={o.id} data-testid={`msg-option-${o.name}`}
          className={`block border rounded-xl p-3 cursor-pointer transition-colors ${selected === o.id ? "border-emerald-500 bg-emerald-50/50" : "border-zinc-200 hover:border-zinc-300"}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <input type="radio" name="msg-option" checked={selected === o.id} onChange={() => onSelect(o.id)} />
            <span className="text-xs font-semibold text-zinc-800">{o.name}</span>
          </div>
          <p className="text-xs text-zinc-600 whitespace-pre-wrap leading-relaxed" data-testid={`msg-preview-${o.name}`}>{o.preview}</p>
        </label>
      ))}
    </div>
  );
}

function Dialog({ req, onClose }) {
  const [data, setData] = useState(null);
  const [send, setSend] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const params = { kind: req.kind, promoter_id: req.promoterId };
    if (req.shiftId) params.shift_id = req.shiftId;
    api.get(`/events/${req.eventId}/message-options`, { params })
      .then((r) => { setData(r.data); setSelected(r.data.options?.[0]?.id || null); })
      .catch(() => setData({ options: [], whatsapp_ready: false }));
  }, [req]);

  const opt = data?.options?.find((o) => o.id === selected);
  const canSend = data?.whatsapp_ready && !!opt;
  const submit = () => onClose({
    send_message: send && canSend,
    registry_template_id: send && opt?.source === "meta" ? opt.id : null,
    template_id: send && opt?.source === "content" ? opt.id : null,
  });

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="message-choice-dialog" onClick={() => onClose(null)}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-100">
          <div>
            <h3 className="font-display font-bold text-zinc-950">{req.title}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">{req.promoterName}</p>
          </div>
          <button onClick={() => onClose(null)} data-testid="message-choice-close" className="p-1 text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          {!data ? <Loader2 className="w-5 h-5 animate-spin text-zinc-400" /> : (
            <>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                <input type="checkbox" checked={send && canSend} disabled={!canSend} onChange={(e) => setSend(e.target.checked)} data-testid="message-choice-send-toggle" />
                <MessageCircle className="w-4 h-4 text-emerald-600" /> Enviar mensagem ao promotor
              </label>
              {!data.whatsapp_ready && <p className="text-xs text-amber-700 flex gap-1.5" data-testid="message-choice-wa-off"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> WhatsApp não ligado — não será enviada mensagem.</p>}
              {data.whatsapp_ready && !data.options.length && <p className="text-xs text-amber-700 flex gap-1.5" data-testid="message-choice-no-template"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Sem template aprovado para esta mensagem.</p>}
              <OptionList options={data.options} selected={selected} onSelect={setSelected} disabled={!send || !canSend} />
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-zinc-100">
          <button onClick={() => onClose(null)} data-testid="message-choice-cancel" className="px-4 py-2 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">Cancelar</button>
          <button onClick={submit} disabled={!data} data-testid="message-choice-confirm" className="px-4 py-2 rounded-lg bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold disabled:opacity-50">{req.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ask({eventId, promoterId, promoterName, shiftId, kind, title, confirmLabel}) → Promise<choice|null>
export function useMessageChoice() {
  const [req, setReq] = useState(null);
  const resolver = useRef(null);
  const ask = useCallback((r) => new Promise((resolve) => { resolver.current = resolve; setReq(r); }), []);
  const close = (choice) => { setReq(null); resolver.current?.(choice); };
  const dialog = req ? <Dialog req={req} onClose={close} /> : null;
  return [dialog, ask];
}
